import {
  createContext,
  use,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { IntlProvider, useIntl, type IntlConfig } from 'react-intl'

import { log, LogLevel } from '@/shared/logging'

import { loadMessages, type Messages } from './catalog'
import { fallbackLanguage } from './languages'
import {
  resolveLocaleSettings,
  textLocale,
  type BrowserLocale,
  type LocalePreference,
  type LocaleSettings,
} from './locale-settings'

/** Settings together with the messages loaded for their language. */
export interface LoadedCatalog {
  readonly settings: LocaleSettings
  readonly messages: Messages
}

/** The sources Site Management fills; the browser is always known. */
export type LocalePreferenceSource = 'person' | 'site'

interface I18nState {
  readonly settings: LocaleSettings
  readonly setPreference: (
    source: LocalePreferenceSource,
    preference: LocalePreference | null,
  ) => void
}

const I18nContext = createContext<I18nState | null>(null)

/** The browser's settings and their messages, loaded before the first render. */
export async function loadStartingCatalog(browser: BrowserLocale): Promise<LoadedCatalog> {
  const settings = resolveLocaleSettings({ person: null, site: null, browser })
  return { settings, messages: await loadMessages(settings.language) }
}

/**
 * Supplies the catalog and the viewer's settings. A new language is shown only once its messages
 * have loaded; until then, and if they fail to load, the current one stays.
 */
export function I18nProvider(props: {
  readonly browser: BrowserLocale
  readonly initial: LoadedCatalog
  /** Overrides react-intl's handler; tests throw, so a missing message fails them. */
  readonly onError?: IntlConfig['onError']
  readonly children: ReactNode
}) {
  const { browser } = props
  const [person, setPerson] = useState<LocalePreference | null>(null)
  const [site, setSite] = useState<LocalePreference | null>(null)
  const [catalog, setCatalog] = useState({
    language: props.initial.settings.language,
    messages: props.initial.messages,
  })
  const [shown, setShown] = useState(props.initial.settings)

  const wanted = useMemo(
    () => resolveLocaleSettings({ person, site, browser }),
    [person, site, browser],
  )
  const isWaiting = wanted.language !== catalog.language
  const settings = isWaiting ? shown : wanted

  // Remember what is shown, so a language still loading keeps the last locale and time zone.
  if (settings !== shown) {
    setShown(settings)
  }

  useEffect(() => {
    if (!isWaiting) {
      return undefined
    }

    let isCurrent = true
    const { language } = wanted
    const switchLanguage = async () => {
      try {
        const messages = await loadMessages(language)
        if (isCurrent) {
          setCatalog({ language, messages })
        }
      } catch (error: unknown) {
        log(
          LogLevel.Error,
          `The ${language} messages could not be loaded.`,
          error instanceof Error ? error : { error: String(error) },
        )
      }
    }
    void switchLanguage()

    return () => {
      isCurrent = false
    }
  }, [isWaiting, wanted])

  const lang = textLocale(settings)

  // In the commit that changes the text, so no frame shows new text under the old language.
  useLayoutEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const setPreference = useCallback(
    (source: LocalePreferenceSource, preference: LocalePreference | null) => {
      // An equal value keeps the old one, so setting it on every render changes nothing.
      const update = (current: LocalePreference | null) =>
        current?.culture === preference?.culture && current?.timeZone === preference?.timeZone
          ? current
          : preference
      if (source === 'person') {
        setPerson(update)
      } else {
        setSite(update)
      }
    },
    [],
  )

  const state = useMemo<I18nState>(() => ({ settings, setPreference }), [settings, setPreference])

  return (
    <I18nContext value={state}>
      <IntlProvider
        locale={lang}
        timeZone={settings.timeZone}
        messages={catalog.messages}
        defaultLocale={fallbackLanguage}
        {...(props.onError === undefined ? {} : { onError: props.onError })}
      >
        {props.children}
      </IntlProvider>
    </I18nContext>
  )
}

function useI18nState(): I18nState {
  const state = use(I18nContext)
  if (state === null) {
    throw new Error('The i18n hooks must be used inside an I18nProvider.')
  }
  return state
}

/** The language, locale and time zone in effect. */
export function useLocaleSettings(): LocaleSettings {
  return useI18nState().settings
}

/** The culture numbers are formatted in, even when its messages fall back to another language; outside an I18nProvider, the messages' language. */
export function useFormattingLocale(): string {
  const state = use(I18nContext)
  const intl = useIntl()
  return state?.settings.locale ?? intl.locale
}

/**
 * Sets the person's or the site's culture and time zone (`null` clears it). The seam Site
 * Management fills; until then only the browser's are used.
 */
export function useSetLocalePreference(): I18nState['setPreference'] {
  return useI18nState().setPreference
}
